import {
	Component,
	ComponentRef,
	DestroyRef,
	effect,
	forwardRef,
	input,
	OnDestroy,
	signal,
	viewChild,
	ViewContainerRef,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import {
	ControlValueAccessor,
	FormControl,
	NG_VALUE_ACCESSOR,
} from '@angular/forms'
import type * as NgxEditor from 'ngx-editor'

@Component({
	selector: 'lowgile-rich-text-editor',
	template: `
		<div class="NgxEditor__Wrapper" (focusout)="onTouched()">
			<ng-container #editorMenu></ng-container>
			<ng-container #editor></ng-container>
		</div>
	`,
	styleUrls: ['./rich-text-editor.component.scss'],
	standalone: false,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => RichTextEditorComponent),
			multi: true,
		},
	],
})
export class RichTextEditorComponent
	implements OnDestroy, ControlValueAccessor
{
	private static editorImportPromise: Promise<typeof NgxEditor> | undefined
	private editor = signal<NgxEditor.Editor | undefined>(undefined)
	private htmlInternal = ''
	private writtenHtml = signal('')
	private disabled = signal(false)
	private onChange: (value: string) => void = () => {}
	protected onTouched: () => void = () => {}

	formControl = input<FormControl>()
	placeholder = input<string>()
	required = input<boolean>(false)

	editorContainerRef = viewChild('editor', { read: ViewContainerRef })
	menuContainerRef = viewChild('editorMenu', { read: ViewContainerRef })
	editorComponentRef = signal<
		ComponentRef<NgxEditor.NgxEditorComponent> | undefined
	>(undefined)
	menuComponent = signal<NgxEditor.MenuComponent | undefined>(undefined)

	constructor(
		protected viewContainerRef: ViewContainerRef,
		private destroyRef: DestroyRef,
	) {
		if (!RichTextEditorComponent.editorImportPromise) {
			RichTextEditorComponent.editorImportPromise = import('ngx-editor')
		}

		effect(async () => {
			const editorContainerRef = this.editorContainerRef()
			const menuContainerRef = this.menuContainerRef()

			if (editorContainerRef && menuContainerRef) {
				const editorImport = await RichTextEditorComponent.editorImportPromise!

				const editor = new editorImport.Editor()
				editor.valueChanges
					.pipe(takeUntilDestroyed(this.destroyRef))
					.subscribe((jsonDoc) => {
						this.htmlInternal = editorImport.toHTML(jsonDoc)
						const htmlOutput =
							this.htmlInternal === '<p></p>' ? '' : this.htmlInternal
						this.onChange(htmlOutput)
					})
				this.editor.set(editor)

				const editorComponentRef = editorContainerRef?.createComponent(
					editorImport.NgxEditorComponent,
				)
				editorComponentRef?.setInput('editor', editor)
				this.editorComponentRef.set(editorComponentRef)

				const menuComponentRef = menuContainerRef?.createComponent(
					editorImport.MenuComponent,
				)
				menuComponentRef?.setInput('editor', editor)
				this.menuComponent.set(menuComponentRef?.instance)
			}
		})

		effect(() => {
			const editor = this.editor()
			if (editor) {
				const html = this.writtenHtml()
				if (html != this.htmlInternal) {
					editor.setContent(html)
				}
			}
		})

		effect(() => {
			const editorComponentRef = this.editorComponentRef()
			const menuComponent = this.menuComponent()
			const disabled = this.disabled()
			const placeholder = this.placeholder()
			if (editorComponentRef) {
				setTimeout(() => {
					editorComponentRef.instance.setDisabledState(disabled)
					if (menuComponent) {
						menuComponent.disabled = disabled
					}
					editorComponentRef.setInput('placeholder', placeholder)
				})
			}
		})
	}

	writeValue(value: string | null): void {
		this.writtenHtml.set(value ?? '')
	}

	registerOnChange(fn: (value: string) => void): void {
		this.onChange = fn
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn
	}

	setDisabledState(isDisabled: boolean): void {
		this.disabled.set(isDisabled)
	}

	ngOnDestroy() {
		this.editor()?.destroy()
	}
}
